import type { AxiosError } from 'axios';

/** Only these two error codes exist on the backend; everything else branches on statusCode. */
export type ApiErrorCode = 'AUTH_THROTTLED' | 'ADMIN_TOTP_ENROLLMENT_REQUIRED';

export interface ApiErrorBody {
  statusCode: number;
  message: string | string[];
  error?: string;
  code?: ApiErrorCode;
  retryAfterSeconds?: number;
}

export interface ParsedApiError {
  status: number | null;
  code: ApiErrorCode | null;
  /** DTO validation errors come back as an array — one entry per line. */
  messages: string[];
  message: string;
  retryAfterSeconds: number | null;
}

export function parseApiError(err: unknown, fallback = 'An unexpected error occurred'): ParsedApiError {
  const axiosErr = err as AxiosError<Partial<ApiErrorBody>> | undefined;
  const body = axiosErr?.response?.data;
  const raw = body?.message ?? (axiosErr as any)?.message;
  const messages = (Array.isArray(raw) ? raw : raw ? [String(raw)] : []).filter(Boolean);
  if (messages.length === 0) messages.push(fallback);

  return {
    status: axiosErr?.response?.status ?? body?.statusCode ?? null,
    code: body?.code ?? null,
    messages,
    message: messages.join('\n'),
    retryAfterSeconds: typeof body?.retryAfterSeconds === 'number' ? body.retryAfterSeconds : null,
  };
}

export function isThrottledError(err: unknown): boolean {
  const parsed = parseApiError(err);
  return parsed.code === 'AUTH_THROTTLED' || parsed.status === 429;
}
